// ============================================================
// Amenities.js — drop into src/ alongside the existing screens.
// Follows the exact patterns already used in App.js:
//   - m(path, opts) for authenticated fetch calls
//   - toast(msg, type) prop for feedback
//   - Accordion expand pattern (like the guidebook's "La maison"
//     tab) rather than a modal, since editing an amenity is a
//     multi-field ongoing task better suited to staying open
//     inline than a modal round-trip
// ============================================================

import { useState, useEffect, useCallback, useRef } from 'react';

const LANGS = [
  { code: 'fr', label: 'FR' },
  { code: 'en', label: 'EN' },
  { code: 'es', label: 'ES' },
  { code: 'ar', label: 'AR' },
];

// Icon set matching the guidebook frontend's inline SVGs.
// Keep in sync with docs/ICONS.md — same keys, same paths.
const ICONS = {
  ac: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M18.4 5.6L17 7M7 17l-1.4 1.4"/><circle cx="12" cy="12" r="4"/></svg>,
  washer: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="4" y="3" width="16" height="18" rx="2"/><circle cx="12" cy="13" r="5"/><circle cx="8" cy="6.5" r="0.6" fill="currentColor"/></svg>,
  dryer: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="4" y="3" width="16" height="18" rx="2"/><circle cx="12" cy="13" r="5"/><path d="M9 13a3 3 0 013-3"/></svg>,
  coffee: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 9h13v4a5 5 0 01-5 5H9a5 5 0 01-5-5z"/><path d="M17 10h1.5a2.5 2.5 0 010 5H17"/><path d="M8 3v2M11 3v2"/></svg>,
  dishwasher: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="4" y="3" width="16" height="18" rx="2"/><path d="M4 9h16"/><circle cx="8" cy="6" r="0.6" fill="currentColor"/></svg>,
  oven: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="4" y="3" width="16" height="18" rx="2"/><rect x="6" y="11" width="12" height="7" rx="1"/><circle cx="8" cy="6.5" r="0.6" fill="currentColor"/></svg>,
  stove: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="9" cy="10" r="2.5"/><circle cx="16" cy="15" r="2.5"/></svg>,
  microwave: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="6" width="18" height="12" rx="2"/><rect x="6" y="9" width="9" height="6" rx="1"/><circle cx="18" cy="12" r="0.6" fill="currentColor"/></svg>,
  fridge: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="5" y="2" width="14" height="20" rx="2"/><path d="M5 9h14"/><circle cx="8" cy="5.5" r="0.6" fill="currentColor"/></svg>,
  tv: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="4" width="18" height="13" rx="2"/><path d="M9 21h6M12 17v4"/></svg>,
  wifi: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 8.5a16 16 0 0118 0"/><path d="M6.5 12.5a11 11 0 0111 0"/><path d="M10 16.5a5.5 5.5 0 014 0"/><circle cx="12" cy="19.5" r="1" fill="currentColor" stroke="none"/></svg>,
  heater: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="4" y="4" width="16" height="16" rx="2"/><path d="M8 8v8M12 8v8M16 8v8"/></svg>,
  'water-heater': <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="7" y="2" width="10" height="20" rx="4"/><path d="M10 8s1-1 1-2-1-2-1-2M14 8s1-1 1-2-1-2-1-2"/></svg>,
  safe: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="12" cy="12" r="4"/><path d="M12 10v2l1.5 1"/></svg>,
  iron: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M4 14c0-4 3-9 8-9s8 5 8 9"/><path d="M4 14h16v3a3 3 0 01-3 3H7a3 3 0 01-3-3z"/></svg>,
  'hair-dryer': <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><circle cx="9" cy="9" r="5"/><path d="M14 9h3a3 3 0 013 3v1"/></svg>,
  lock: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="4" y="10" width="16" height="10" rx="1.5"/><path d="M8 10V7a4 4 0 018 0v3"/></svg>,
  intercom: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="6" y="3" width="12" height="18" rx="2"/><circle cx="12" cy="8" r="2"/><path d="M9 14h6M9 17h6"/></svg>,
  elevator: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="5" y="2" width="14" height="20" rx="1"/><path d="M10 8l2-2 2 2M10 16l2 2 2-2"/></svg>,
  parking: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 16V8h3.5a2.5 2.5 0 010 5H9"/></svg>,
  pool: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 17c1.5 1 3 1 4.5 0s3-1 4.5 0 3 1 4.5 0 3-1 4.5 0"/><path d="M6 13V6a2 2 0 012-2h8a2 2 0 012 2v7"/></svg>,
  bbq: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><ellipse cx="12" cy="8" rx="7" ry="3"/><path d="M6 8v6a6 3 0 0012 0V8"/><path d="M9 20v-3M15 20v-3"/></svg>,
};

const IconChevron = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M6 9l6 6 6-6"/></svg>;
const IconTrash = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/></svg>;
const IconUp = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M12 19V5M5 12l7-7 7 7"/></svg>;
const IconDown = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M12 5v14M5 12l7 7 7-7"/></svg>;
const IconUpload = <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 15V3M8 7l4-4 4 4"/><path d="M4 15v4a2 2 0 002 2h12a2 2 0 002-2v-4"/></svg>;

function LangTabs({ activeLang, onChange, filledLangs }) {
  return (
    <div className="lang-tabs">
      {LANGS.map(l => (
        <button
          key={l.code}
          className={`lang-tab ${activeLang === l.code ? 'on' : ''} ${filledLangs.includes(l.code) ? 'filled' : ''}`}
          onClick={() => onChange(l.code)}
          type="button"
        >
          {l.label}
          <span className="flag-dot" />
        </button>
      ))}
    </div>
  );
}

function StepRow({ step, onUpdate, onDelete, onMoveUp, onMoveDown, isFirst, isLast, activeLang, m }) {
  const fileInputRef = useRef(null);

  const handleFileSelect = async (file, mediaType) => {
    if (!file) return;
    // Uses the same upload pattern as logo/stamp uploads elsewhere
    // in the dashboard — multipart FormData to a dedicated endpoint.
    // Backend route for step media upload is not yet built; this
    // wires the UI contract so the backend can be added without
    // touching this component.
    const formData = new FormData();
    formData.append('media', file);
    formData.append('media_type', mediaType);
    try {
      const res = await m(`/admin/steps/${step.id}/media`, { method: 'POST', body: formData });
      const data = await res.json();
      if (data.success) {
        onUpdate({ media_type: mediaType, media_url: data.url });
      }
    } catch (e) {
      // Upload endpoint not yet live — fall back to a local preview
      // so the step text/reorder flow can still be tested.
      const localUrl = URL.createObjectURL(file);
      onUpdate({ media_type: mediaType, media_url: localUrl });
    }
  };

  return (
    <div className="step-row">
      <div
        className="step-media-preview"
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files[0];
            if (!file) return;
            const mediaType = file.type.startsWith('video') ? 'video' : 'photo';
            handleFileSelect(file, mediaType);
          }}
        />
        {step.media_url ? (
          <>
            {step.media_type === 'video' ? (
              <div style={{ width: '100%', height: '100%', background: '#1A1A1A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <svg width="8" height="8" viewBox="0 0 24 24" fill="#1A1A1A"><path d="M8 5v14l11-7z" /></svg>
                </div>
              </div>
            ) : (
              <img src={step.media_url} alt="" />
            )}
            <span className="media-type-badge">{step.media_type === 'video' ? 'Vidéo' : 'Photo'}</span>
          </>
        ) : (
          <div className="step-media-empty">
            {IconUpload}
            <span>Ajouter</span>
          </div>
        )}
      </div>

      <div className="step-content">
        <textarea
          className="step-text-input"
          value={step.text?.[activeLang] || ''}
          onChange={(e) => onUpdate({ text: { ...step.text, [activeLang]: e.target.value } })}
          placeholder={activeLang === 'fr' ? 'Texte de cette étape (requis)' : `Texte en ${activeLang.toUpperCase()}`}
          rows={2}
        />
      </div>

      <div className="step-actions">
        <button className="step-icon-btn" onClick={onMoveUp} disabled={isFirst} title="Monter" type="button">{IconUp}</button>
        <button className="step-icon-btn" onClick={onMoveDown} disabled={isLast} title="Descendre" type="button">{IconDown}</button>
        <button className="step-icon-btn danger" onClick={onDelete} title="Supprimer" type="button">{IconTrash}</button>
      </div>
    </div>
  );
}

function AmenityCard({ amenity, onUpdate, onDelete, onAddStep, onUpdateStep, onDeleteStep, onReorderSteps, m, toast }) {
  const [open, setOpen] = useState(false);
  const [activeLang, setActiveLang] = useState('fr');

  const filledNameLangs = LANGS.filter(l => amenity.name?.[l.code]).map(l => l.code);
  const icon = ICONS[amenity.icon] || ICONS.ac;

  const moveStep = (index, direction) => {
    const newOrder = [...amenity.steps];
    const target = index + direction;
    if (target < 0 || target >= newOrder.length) return;
    [newOrder[index], newOrder[target]] = [newOrder[target], newOrder[index]];
    onReorderSteps(newOrder.map(s => s.id));
  };

  return (
    <div className={`amenity-card ${open ? 'open' : ''}`}>
      <div className="amenity-card-head" onClick={() => setOpen(!open)}>
        <div className="amenity-card-icon">{icon}</div>
        <div className="amenity-card-titles">
          <div className="amenity-card-name">{amenity.name?.fr || 'Sans nom'}</div>
          <div className="amenity-card-meta">{amenity.steps.length} étape{amenity.steps.length !== 1 ? 's' : ''}</div>
        </div>
        <div className="amenity-card-actions" onClick={(e) => e.stopPropagation()}>
          <button
            className="step-icon-btn danger"
            onClick={() => window.confirm('Supprimer cet équipement ?') && onDelete()}
            type="button"
            title="Supprimer"
          >
            {IconTrash}
          </button>
        </div>
        <div className="amenity-card-chevron">{IconChevron}</div>
      </div>

      {open && (
        <div className="amenity-card-body">
          <LangTabs activeLang={activeLang} onChange={setActiveLang} filledLangs={filledNameLangs} />

          <div className="ff">
            <label className="fl">Nom {activeLang === 'fr' ? '(requis)' : `(${activeLang.toUpperCase()})`}</label>
            <input
              className="fi"
              value={amenity.name?.[activeLang] || ''}
              onChange={(e) => onUpdate({ name: { ...amenity.name, [activeLang]: e.target.value } })}
              placeholder={activeLang === 'fr' ? 'Climatisation' : `Nom en ${activeLang.toUpperCase()}`}
            />
          </div>

          <div className="ff">
            <label className="fl">Étapes</label>
            <div className="steps-list">
              {amenity.steps.map((step, i) => (
                <StepRow
                  key={step.id}
                  step={step}
                  activeLang={activeLang}
                  m={m}
                  onUpdate={(patch) => onUpdateStep(step.id, patch)}
                  onDelete={() => onDeleteStep(step.id)}
                  onMoveUp={() => moveStep(i, -1)}
                  onMoveDown={() => moveStep(i, 1)}
                  isFirst={i === 0}
                  isLast={i === amenity.steps.length - 1}
                />
              ))}
            </div>
            <button className="add-step-btn" onClick={onAddStep} type="button">
              + Ajouter une étape
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Amenities({ propertyId, propertyName, m, toast }) {
  const [amenities, setAmenities] = useState([]);
  const [library, setLibrary] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    if (!propertyId) return;
    setLoading(true);
    Promise.all([
      m(`/admin/properties/${propertyId}/amenities`).then(r => r.json()),
      m('/admin/amenity-types').then(r => r.json()),
    ])
      .then(([amenitiesRes, libraryRes]) => {
        if (amenitiesRes.success) setAmenities(amenitiesRes.data);
        if (libraryRes.success) setLibrary(libraryRes.data);
      })
      .catch(() => toast('Erreur de chargement', 'err'))
      .finally(() => setLoading(false));
  }, [propertyId, m, toast]);

  useEffect(() => { load(); }, [load]);

  const addFromLibrary = async (type) => {
    try {
      const res = await m(`/admin/properties/${propertyId}/amenities`, {
        method: 'POST',
        body: JSON.stringify({ amenity_type_id: type.id, name: { fr: type.default_name_fr } }),
      });
      const data = await res.json();
      if (data.success) { toast('Équipement ajouté', 'ok'); load(); }
      else toast(data.error || 'Erreur', 'err');
    } catch (e) { toast('Erreur réseau', 'err'); }
  };

  const addCustom = async () => {
    const name = window.prompt('Nom du nouvel équipement (français) :');
    if (!name || !name.trim()) return;
    try {
      const res = await m(`/admin/properties/${propertyId}/amenities`, {
        method: 'POST',
        body: JSON.stringify({ icon: 'ac', name: { fr: name.trim() } }),
      });
      const data = await res.json();
      if (data.success) { toast('Équipement ajouté', 'ok'); load(); }
      else toast(data.error || 'Erreur', 'err');
    } catch (e) { toast('Erreur réseau', 'err'); }
  };

  const updateAmenity = async (id, patch) => {
    setAmenities(prev => prev.map(a => a.id === id ? { ...a, ...patch } : a));
    try {
      await m(`/admin/amenities/${id}`, { method: 'PATCH', body: JSON.stringify(patch) });
    } catch (e) { toast('Erreur de sauvegarde', 'err'); }
  };

  const deleteAmenity = async (id) => {
    try {
      const res = await m(`/admin/amenities/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) { toast('Équipement supprimé', 'ok'); load(); }
      else toast(data.error || 'Erreur', 'err');
    } catch (e) { toast('Erreur réseau', 'err'); }
  };

  const addStep = async (amenityId) => {
    try {
      const res = await m(`/admin/amenities/${amenityId}/steps`, {
        method: 'POST',
        body: JSON.stringify({ media_type: 'none', text: { fr: '' } }),
      });
      const data = await res.json();
      if (data.success) load();
      else toast(data.error || 'Erreur', 'err');
    } catch (e) { toast('Erreur réseau', 'err'); }
  };

  const updateStep = async (stepId, patch) => {
    setAmenities(prev => prev.map(a => ({
      ...a,
      steps: a.steps.map(s => s.id === stepId ? { ...s, ...patch } : s),
    })));
    try {
      await m(`/admin/steps/${stepId}`, { method: 'PATCH', body: JSON.stringify(patch) });
    } catch (e) { toast('Erreur de sauvegarde', 'err'); }
  };

  const deleteStep = async (stepId) => {
    try {
      await m(`/admin/steps/${stepId}`, { method: 'DELETE' });
      load();
    } catch (e) { toast('Erreur réseau', 'err'); }
  };

  const reorderSteps = async (amenityId, order) => {
    setAmenities(prev => prev.map(a => a.id === amenityId
      ? { ...a, steps: order.map(id => a.steps.find(s => s.id === id)) }
      : a));
    try {
      await m(`/admin/amenities/${amenityId}/steps/reorder`, { method: 'PATCH', body: JSON.stringify({ order }) });
    } catch (e) { toast('Erreur de sauvegarde', 'err'); }
  };

  if (!propertyId) {
    return (
      <div className="empty">
        <div className="empty-t">Sélectionnez une propriété</div>
        <div className="empty-s">Choisissez une propriété pour gérer ses équipements</div>
      </div>
    );
  }

  return (
    <div className="amenities-layout">
      <div className="amenity-library">
        <div className="amenity-library-title">Bibliothèque</div>
        <div className="amenity-lib-grid">
          {library.map(type => (
            <div key={type.id} className="amenity-lib-item" onClick={() => addFromLibrary(type)}>
              <div className="amenity-lib-icon">{ICONS[type.default_icon] || ICONS.ac}</div>
              <div className="amenity-lib-name">{type.default_name_fr}</div>
            </div>
          ))}
        </div>
        <button className="btn btn-s amenity-lib-custom" onClick={addCustom} type="button">
          + Équipement personnalisé
        </button>
      </div>

      <div>
        <div className="card-hdr" style={{ background: 'none', border: 'none', padding: '0 0 12px' }}>
          <div className="card-title">Équipements — {propertyName}</div>
        </div>
        {loading ? (
          <div className="empty"><span className="spin" /></div>
        ) : amenities.length === 0 ? (
          <div className="empty">
            <div className="empty-ico"><i className="ti ti-tool" style={{ fontSize: 26 }} /></div>
            <div className="empty-t">Aucun équipement</div>
            <div className="empty-s">Ajoutez-en depuis la bibliothèque à gauche</div>
          </div>
        ) : (
          <div className="amenity-list">
            {amenities.map(amenity => (
              <AmenityCard
                key={amenity.id}
                amenity={amenity}
                m={m}
                toast={toast}
                onUpdate={(patch) => updateAmenity(amenity.id, patch)}
                onDelete={() => deleteAmenity(amenity.id)}
                onAddStep={() => addStep(amenity.id)}
                onUpdateStep={(stepId, patch) => updateStep(stepId, patch)}
                onDeleteStep={(stepId) => deleteStep(stepId)}
                onReorderSteps={(order) => reorderSteps(amenity.id, order)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
